import type { MouseEvent, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn } from "@/lib/utils";
import {
	isEnabledAccountPage,
	isEnabledProfilePage,
} from "@/lib/utils/features";
import { authGradients } from "@/shared/constants/common";
import { ROUTES } from "@/shared/constants/router";
import { LocalStorageKeys } from "@/shared/types/common";
import sprites from "/src/shared/assets/sprite.svg";
import { ChatIconWithPhoneForm } from "../../../shared/components/ChatIconWithPhoneForm";
import { Button } from "../../ui/button";
import {
	Menubar,
	MenubarContent,
	MenubarItem,
	MenubarMenu,
	MenubarPortal,
	MenubarTrigger,
} from "../../ui/menubar";

export const Header = ({ content }: { content?: ReactNode }) => {
	const { authService, isAuthenticated, login, logout } = useAuth();
	const location = useLocation();
	const [, setAttemptToBuy] = useLocalStorage(
		LocalStorageKeys.attemptToBuy,
		null,
	);

	const navigate = useNavigate();

	const handleNavigateToDashboard = (e: MouseEvent) => {
		e.preventDefault();

		if (location.pathname !== ROUTES.DASHBOARD) {
			navigate(ROUTES.DASHBOARD);
			setAttemptToBuy(null); // to show the survey for new users, if he is not premium and not confirmed terms
		}
	};

	const handleNavigateToProfile = (e: MouseEvent) => {
		e.preventDefault();
		navigate(ROUTES.PROFILE);
	};

	const handleNavigateToAccount = (e: Event) => {
		e.preventDefault();
		navigate(ROUTES.ACCOUNT);
	};

	const handleLogin = (e: MouseEvent) => {
		e.preventDefault();
		login();
	};

	const handleLogout = (e: Event | MouseEvent) => {
		e.preventDefault();
		logout();
	};

	const gradientColor = authService?.color
		? authGradients[authService.color]
		: "";

	return (
		<header
			className={cn(
				"flex justify-between items-center h-[var(--header-height)] w-full p-0 pl-1 sticky top-0 bg-gradient-to-tr from-bg-background from-50% z-[49]",
				gradientColor,
			)}
		>
			{content ?? <span className="w-full" />}

			<Button
				className="h-fit min-w-fit bg-transparent hover:bg-transparent p-1 pb-0 mr-12"
				onClick={handleNavigateToDashboard}
			>
				<img
					src="/eps-logo.webp"
					alt="EPSMomentum Logo"
					className="h-5 w-auto"
				/>
			</Button>

			<ChatIconWithPhoneForm />

			{isAuthenticated && isEnabledProfilePage() && (
				<Button
					className="h-fit bg-transparent hover:bg-transparent p-1"
					onClick={handleNavigateToProfile}
				>
					<svg aria-label="profile" role="img" fill="#fff" className="w-4 h-4">
						<use xlinkHref={`${sprites}#profile`} />
					</svg>
				</Button>
			)}

			<div className="mr-3">
				{isAuthenticated ? (
					isEnabledAccountPage() ? (
						<Menubar>
							<MenubarMenu>
								<MenubarTrigger data-testid="menubar-trigger" className="p-1">
									<svg
										aria-label="account-settings"
										role="img"
										fill="#fff"
										className="w-4 h-4"
									>
										<use xlinkHref={`${sprites}#account-settings`} />
									</svg>
								</MenubarTrigger>

								<MenubarPortal>
									<MenubarContent align="end">
										<p className="uppercase text-light-gray text-sm font-medium mb-3">
											Settings
										</p>
										<div className="border-l border-light-gray ml-0.5">
											<MenubarItem onSelect={handleNavigateToAccount}>
												Account
											</MenubarItem>
											<MenubarItem onSelect={handleLogout}>Logout</MenubarItem>
										</div>
									</MenubarContent>
								</MenubarPortal>
							</MenubarMenu>
						</Menubar>
					) : (
						<Button
							variant="outline"
							onClick={handleLogout}
							type="button"
							className="h-[18px]"
						>
							Logout
						</Button>
					)
				) : (
					<Button
						variant="outline"
						onClick={handleLogin}
						type="button"
						className="h-[18px]"
					>
						Login
					</Button>
				)}
			</div>
		</header>
	);
};
